import { Link } from "react-router-dom";
import { ArrowUpRight, Sparkles } from "lucide-react";

// Maps each audit category to the service page that helps fix it.
const CATEGORY_TO_SERVICE = {
  seo: { to: "/improve/seo", label: "SEO & Search Visibility", reason: "Improve how search engines discover and rank this site." },
  performance: { to: "/improve/performance", label: "Website Performance", reason: "Speed up load times and Core Web Vitals." },
  mobile: { to: "/improve/performance", label: "Website Performance", reason: "Tune the experience for real-world mobile visitors." },
  accessibility: { to: "/improve/accessibility", label: "Website Accessibility", reason: "Make the site usable for everyone." },
  content: { to: "/ai/ai-content", label: "AI Content", reason: "Answer the questions your customers are actually asking." },
  conversion: { to: "/improve/conversion", label: "Conversion Optimization", reason: "Turn more of your existing traffic into leads." },
  security: { to: "/improve/website-audit", label: "Website Audit", reason: "Harden security fundamentals and fix technical issues." },
  ai_readiness: { to: "/ai/ai-readiness-audit", label: "AI Readiness Audit", reason: "See which AI features would create real value here." },
};

const LABELS = {
  seo: "SEO", performance: "Performance", mobile: "Mobile", accessibility: "Accessibility",
  content: "Content", conversion: "Conversion", security: "Security", ai_readiness: "AI Readiness",
};

const tone = (score) => (score >= 80 ? "#16A34A" : score >= 60 ? "#F59E0B" : "#EF4444");

export default function ServiceRecommendations({ categories }) {
  if (!categories) return null;

  // Rank categories worst-first, recommend fixes for the 3 lowest scores.
  const ranked = Object.entries(categories)
    .filter(([key]) => CATEGORY_TO_SERVICE[key])
    .sort((a, b) => a[1] - b[1])
    .slice(0, 3);

  if (ranked.length === 0) return null;

  return (
    <div className="mt-6 bg-white border border-slate-200 rounded-2xl card-shadow p-6 md:p-8" data-testid="service-recommendations">
      <div className="flex items-center gap-2 mb-1">
        <Sparkles size={16} className="text-brand" />
        <span className="overline">Recommended Next Steps</span>
      </div>
      <p className="font-heading text-xl md:text-2xl font-bold text-slate-900">Where WebsiteTech can help first</p>
      <p className="text-slate-500 text-sm mt-1">Based on your lowest-scoring categories, these services will move the needle fastest.</p>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-6">
        {ranked.map(([key, score]) => {
          const svc = CATEGORY_TO_SERVICE[key];
          return (
            <Link key={key} to={svc.to} data-testid={`reco-${key}`} className="group border border-slate-200 rounded-xl p-5 hover:border-brand hover-lift transition-colors flex flex-col">
              <div className="flex items-center justify-between mb-3">
                <span className="text-[11px] font-bold uppercase tracking-wide text-slate-400">{LABELS[key]}</span>
                <span className="font-mono text-lg font-bold" style={{ color: tone(score) }}>{score}</span>
              </div>
              <h4 className="font-heading text-base font-bold text-slate-900 flex items-center justify-between gap-2">
                {svc.label}
                <ArrowUpRight size={16} className="text-slate-300 group-hover:text-brand transition-colors shrink-0" />
              </h4>
              <p className="text-sm text-slate-500 mt-1.5 flex-1">{svc.reason}</p>
            </Link>
          );
        })}
      </div>
    </div>
  );
}
